import { createRoute, useLoaderData } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import { abMonitorPageQueryOptions } from "~/app/api/pageData/abMonitorPageQuery";
import type {
  AbMonitorAccountData,
  AbMonitorFeedbackOptionParentData,
  AbMonitorPageInitialData,
} from "~/app/api/pageData/abMonitorPageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import { createOrderbookPage, type ABMonitorPageProps } from "~/pages/OrderbookPage/OrderbookPage";
import type { orderColumnPreferences } from "~/features/orderbook/models/orderbookModels";
import type {
  FeedbackOption,
  FeedbackOptionCategory,
  UserData,
} from "~/features/orders/orderDetails/hooks/useSendOrderFeedback/components/OrderListFeedbackContext";
import type {
  MultiAccountAccountData,
  MultiAccountEndCustomerData,
} from "~/features/orders/orderList/components/OrderListSearchBox/MultiAccountSelect/MultiAccountSearchDataProvider";

function toFeedbackChildren(options: { label: string; value: string }[]): FeedbackOption[] {
  return options.map((option) => ({ label: option.label, value: option.value }));
}

function toFeedbackOptionCategories(options: AbMonitorFeedbackOptionParentData[]): FeedbackOptionCategory[] {
  return options.map((option, index) => ({
    label: option.label,
    value: String(index),
    title: option.title,
    children: toFeedbackChildren(option.children),
  }));
}

function toEndCustomers(endCustomers: { code: string; name: string }[]): MultiAccountEndCustomerData[] {
  return endCustomers.map((endCustomer) => ({ code: endCustomer.code, name: endCustomer.name }));
}

function toAccounts(accounts: AbMonitorAccountData[] | undefined): { accounts: MultiAccountAccountData[] } | undefined {
  if (!accounts) {
    return undefined;
  }
  return {
    accounts: accounts.map((account) => ({
      code: account.code,
      name: account.name ?? "",
      shortCode: account.shortCode,
      salesOrg: account.salesOrg,
      endCustomer: toEndCustomers(account.endCustomers),
    })),
  };
}

function toOrderBookPreferences(preferences: string): orderColumnPreferences[] {
  if (preferences.trim() === "") {
    return [];
  }
  try {
    const parsed = JSON.parse(preferences);
    return Array.isArray(parsed) ? (parsed as orderColumnPreferences[]) : [];
  } catch {
    return [];
  }
}

function toUserData(userData: Record<string, string>): UserData {
  return {
    uid: userData.uid ?? "",
    firstName: userData.firstName ?? "",
    lastName: userData.lastName ?? "",
    email: userData.email ?? "",
    phone: userData.phone ?? "",
  };
}

function toAbMonitorPageProps(data: AbMonitorPageInitialData | undefined): ABMonitorPageProps {
  if (!data) {
    throw new Error("AB monitor page-data response is missing its required body");
  }
  return {
    accounts: toAccounts(data.accounts),
    showUnconfirmedLinesFilter: data.showUnconfirmedLinesFilter,
    orderBookPreferences: toOrderBookPreferences(data.orderBookPreferences),
    userData: toUserData(data.userData),
    orderFeedbackOptions: toFeedbackOptionCategories(data.feedbackOptions),
  };
}

function AbMonitorRoute() {
  const result = useLoaderData({
    from: "/open-orders",
  }) as PageInitialData<AbMonitorPageInitialData>;
  const shellProps = useStorefrontShellProps();

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {createOrderbookPage(toAbMonitorPageProps(result.data))}
    </StorefrontShellFrame>
  );
}

export async function loadAbMonitorPageData(context: { queryClient: QueryClient }) {
  const result = await context.queryClient.ensureQueryData(abMonitorPageQueryOptions());

  return unwrapPageInitialData(result);
}

export const abMonitorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/open-orders",
  loader: async ({ context }) => loadAbMonitorPageData(context),
  component: AbMonitorRoute,
});
